import { useCallback, useEffect, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { ustIdPruefen, type UstIdAntwort } from "../../services/firmendatenService";
import { VERTRAG_FELDER, zusammenfuehren } from "./GesellschaftsvertragDialog";

/**
 * Die Umsatzsteuer-Identifikationsnummer beim europäischen
 * Bestätigungsverfahren gegenprüfen.
 *
 * Sie steht nie im Handelsregister — sie unterliegt dem Steuergeheimnis. In
 * KMVS kommt sie aus dem Impressum, und dort steht sie so, wie eine Agentur sie
 * abgetippt hat. Die Gegenprobe ist kostenfrei und ohne Kontingent.
 *
 * **Zwei Dinge, die das Ergebnis NICHT sagt.** Für eine deutsche Nummer gibt
 * das Verfahren weder Namen noch Anschrift heraus — bestätigt ist nur, dass sie
 * vergeben ist. Und aus einer gültigen Nummer folgt die
 * Vorsteuerabzugsberechtigung nur als Regelvermutung: Wer ausschließlich
 * steuerfreie Umsätze erzielt, ist es nicht und kann die Nummer trotzdem führen.
 */
const useStyles = makeStyles({
    ergebnis: {
        display: "flex",
        alignItems: "center",
        ...shorthands.gap("10px"),
        ...shorthands.padding("12px", "14px"),
        ...shorthands.borderRadius("10px"),
        marginBottom: "14px",
        fontSize: "14px",
        lineHeight: 1.45,
    },
    gueltig: { backgroundColor: "#eef7f1", color: "#1f5c3d" },
    ungueltig: { backgroundColor: "#fdf0ee", color: "#8a2f22" },
    zeichen: { fontSize: "20px", lineHeight: 1, flexShrink: 0 },
    nummer: {
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontWeight: 700,
        letterSpacing: "0.02em",
    },
    block: {
        ...shorthands.padding("10px", "12px"),
        ...shorthands.borderRadius("9px"),
        backgroundColor: "#f8f4f8",
        marginBottom: "12px",
        fontSize: "13px",
        lineHeight: 1.5,
        color: "#3d2d39",
        whiteSpace: "pre-line",
    },
    beschriftung: { fontSize: "11.5px", fontWeight: 700, color: "#6b5a66" },
    zeile: {
        display: "flex",
        alignItems: "flex-start",
        ...shorthands.gap("10px"),
        ...shorthands.padding("11px", "2px"),
        ...shorthands.borderTop("1px", "solid", "#ece6ec"),
    },
    inhalt: { flexGrow: 1, minWidth: 0 },
    vorschau: { display: "block", fontSize: "12px", color: "#6b5a66", marginTop: "4px", lineHeight: 1.45 },
    hinweis: {
        fontSize: "12.5px",
        color: "#7a5a18",
        backgroundColor: "#fdf6e3",
        ...shorthands.padding("8px", "10px"),
        ...shorthands.borderRadius("8px"),
        marginTop: "10px",
        lineHeight: 1.45,
    },
    fehler: { fontSize: "12.5px", color: "#b42318", marginTop: "10px" },
    mitte: { ...shorthands.padding("22px", "0"), textAlign: "center", color: "#6b5a66", fontSize: "13px" },
    knopf: {
        display: "inline-flex",
        alignItems: "center",
        ...shorthands.gap("8px"),
        ...shorthands.padding("9px", "14px"),
        ...shorthands.borderRadius("9px"),
        ...shorthands.border("1.5px", "solid", "#ddd3dc"),
        backgroundColor: "#fff",
        color: "#7d2169",
        fontFamily: '"Hanken Grotesk", sans-serif',
        fontSize: "12.5px",
        fontWeight: 700,
        lineHeight: 1,
        cursor: "pointer",
        whiteSpace: "nowrap",
        ":hover": { ...shorthands.borderColor("#cd8fbd"), backgroundColor: "#f8eef5" },
        ":disabled": { color: "#a596a1", cursor: "default", backgroundColor: "#f6f3f5" },
    },
    knopfStark: {
        backgroundColor: "#7d2169",
        color: "#fff",
        ...shorthands.borderColor("#7d2169"),
        ":hover": { backgroundColor: "#591149", ...shorthands.borderColor("#591149") },
        ":disabled": {
            backgroundColor: "#d9c6d4",
            ...shorthands.borderColor("#d9c6d4"),
            color: "#fff",
        },
    },
    fuss: { display: "flex", ...shorthands.gap("10px"), justifyContent: "flex-end" },
});

export function UstIdDialog({
    contactId,
    nummer,
    onClose,
    onUebernehmen,
}: {
    contactId: number;
    /** Die Nummer aus der Maske — sie geht vor, auch wenn sie noch nicht gespeichert ist. */
    nummer?: string | null;
    onClose: () => void;
    onUebernehmen: (werte: Array<{ feld: string; wert: string }>) => void;
}) {
    const s = useStyles();
    const [laeuft, setLaeuft] = useState(true);
    const [fehler, setFehler] = useState<string | null>(null);
    const [antwort, setAntwort] = useState<UstIdAntwort | null>(null);
    const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set());

    const pruefen = useCallback(async () => {
        setLaeuft(true);
        setFehler(null);
        try {
            const r = await ustIdPruefen(contactId, nummer);
            setAntwort(r);
            // Nichts vorauswählen: Die Vorsteuerabzugsberechtigung ist eine
            // Vermutung mit einer Ausnahme, die genau diese Kundschaft betrifft.
            setGewaehlt(new Set());
        } catch (e) {
            setFehler(e instanceof Error ? e.message : String(e));
        } finally {
            setLaeuft(false);
        }
    }, [contactId, nummer]);

    useEffect(() => {
        void pruefen();
    }, [pruefen]);

    const uebernehmen = () => {
        if (!antwort) return;
        onUebernehmen(
            antwort.vorschlaege
                .filter((v) => gewaehlt.has(v.feld))
                .map((v) => ({ feld: VERTRAG_FELDER[v.feld], wert: zusammenfuehren(v, v.wert) })),
        );
        onClose();
    };

    return createPortal(
        <div className="kvp-rd-scrim" onClick={laeuft ? undefined : onClose}>
            <div className="dm" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <div className="dm-head">
                    <div className="dm-head-top">
                        <div className="dm-titles">
                            <div className="dm-kicker">EU-Bestätigungsverfahren</div>
                            <h2>Umsatzsteuer-Identifikationsnummer</h2>
                        </div>
                        {!laeuft && (
                            <button className="dm-x" onClick={onClose} aria-label="Schließen">
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round"><path d="M4.5 4.5l9 9M13.5 4.5l-9 9" /></svg>
                            </button>
                        )}
                    </div>
                </div>

                <div className="dm-body">
                    {laeuft && <div className={s.mitte}>Die Nummer wird bei der EU-Kommission geprüft …</div>}

                    {!laeuft && antwort && (
                        <>
                            <div
                                className={mergeClasses(
                                    s.ergebnis,
                                    antwort.gueltig ? s.gueltig : s.ungueltig,
                                )}
                            >
                                <span className={s.zeichen} aria-hidden>
                                    {antwort.gueltig ? "✓" : "✕"}
                                </span>
                                <span>
                                    <span className={s.nummer}>{antwort.nummer}</span> ist{" "}
                                    {antwort.gueltig ? "gültig" : "nicht als gültig hinterlegt"}
                                    {antwort.geprueftAm
                                        ? ` — geprüft am ${new Date(antwort.geprueftAm).toLocaleString("de-DE")}`
                                        : ""}
                                    .
                                </span>
                            </div>

                            {(antwort.name || antwort.anschrift) && (
                                <div className={s.block}>
                                    <span className={s.beschriftung}>Auf diese Angaben ausgestellt</span>
                                    {"\n"}
                                    {antwort.name}
                                    {antwort.anschrift ? `\n${antwort.anschrift}` : ""}
                                    {antwort.namensabgleich === true && "\n\n✓ stimmt mit dem Kontakt überein"}
                                </div>
                            )}

                            {antwort.vorschlaege.map((v) => (
                                <div key={v.feld} className={s.zeile}>
                                    <input
                                        type="checkbox"
                                        checked={gewaehlt.has(v.feld)}
                                        onChange={() =>
                                            setGewaehlt((alt) => {
                                                const neu = new Set(alt);
                                                if (neu.has(v.feld)) neu.delete(v.feld);
                                                else neu.add(v.feld);
                                                return neu;
                                            })
                                        }
                                        aria-label={v.bezeichnung}
                                        style={{ marginTop: 3, accentColor: "#7d2169" }}
                                    />
                                    <span className={s.inhalt}>
                                        <span className={s.beschriftung}>{v.bezeichnung}</span>{" "}
                                        <strong>{v.wert}</strong>
                                        {v.begruendung && <span className={s.vorschau}>{v.begruendung}</span>}
                                    </span>
                                </div>
                            ))}

                            {antwort.hinweise.map((h, i) => (
                                <div key={i} className={s.hinweis}>{h}</div>
                            ))}
                        </>
                    )}

                    {fehler && <div className={s.fehler}>{fehler}</div>}
                </div>

                <div className={mergeClasses("dm-foot", s.fuss)}>
                    <button type="button" className={s.knopf} onClick={onClose}>
                        Schließen
                    </button>
                    {!!antwort?.vorschlaege.length && (
                        <button
                            type="button"
                            className={mergeClasses(s.knopf, s.knopfStark)}
                            disabled={gewaehlt.size === 0}
                            onClick={uebernehmen}
                        >
                            Übernehmen
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}
